import moment from 'moment';
import currencyFormatter from '../../../utils/currencyFormatter';
import {
  buildContrFornExtendedFieldsPdfRows,
  buildContrFornStandardExtraPdfRows,
  buildDocumentosObrigatoriosMinutaItems,
  getContrFornDocumentosObrigatoriosIds,
  isEquipeOrcItemById,
} from './contrFornExtendedFields';
import { getAnexosParaPdf } from './contrFornEmailUtils';

const formatMinutaDate = (value) => (value ? moment(value).format('DD/MM/YYYY') : null);

const formatMinutaText = (value) => {
  if (value == null || value === '') return null;
  const text = String(value).trim();
  return text || null;
};

const normalizeDocumentoLabel = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const PJ_FIXED_DOCUMENTO_LABELS = new Set([
  'cartao cnpj',
  'comprovante de residencia',
  'comprovante de endereco',
  'contrato social',
]);

const isPjFixedDocumentoObrigatorio = (item = {}) => {
  const label = normalizeDocumentoLabel(item.tpDocumento_desc || item.label);
  return PJ_FIXED_DOCUMENTO_LABELS.has(label);
};

const resolveDocumentosObrigatoriosForHash = (contrFornData = {}, documentosObrigatoriosOptions = []) => {
  const ids = getContrFornDocumentosObrigatoriosIds(contrFornData);
  if (ids.length === 0) return null;

  let items = buildDocumentosObrigatoriosMinutaItems(contrFornData, documentosObrigatoriosOptions);
  if (contrFornData.contrForn_parteTab === 'pj') {
    items = items.filter((item) => !isPjFixedDocumentoObrigatorio(item));
  }

  return items.length > 0 ? items : null;
};

const pdfRowsToObject = (rows) => rows.reduce((acc, [label, value]) => {
  acc[label] = value;
  return acc;
}, {});

const sanitizeParcelaForMinuta = (parcela, index) => {
  const { contrFornEvento_nf, ...parcelaBase } = parcela || {};
  const nfMeta = Array.isArray(contrFornEvento_nf) && contrFornEvento_nf.length > 0
    ? contrFornEvento_nf.map((file) => ({
      nome: file?.props?.name || file?.data?.path || null,
    }))
    : undefined;

  return {
    ...parcelaBase,
    contrFornEvento_num: parcela?.contrFornEvento_num ?? index + 1,
    contrFornEvento_desc: parcela?.contrFornEvento_desc ?? null,
    contrFornEvento_dtPrev: parcela?.contrFornEvento_dtPrev ?? null,
    contrFornEvento_dtPrevFormatada: formatMinutaDate(parcela?.contrFornEvento_dtPrev),
    contrFornEvento_dependeAtividade: parcela?.contrFornEvento_dependeAtividade ?? 0,
    contrFornEvento_pgtoPrazoDias: parcela?.contrFornEvento_pgtoPrazoDias ?? null,
    prazoDiasExibicao: parcela?.contrFornEvento_dependeAtividade === 1
      ? formatMinutaText(parcela?.contrFornEvento_pgtoPrazoDias)
      : '-',
    contrFornEvento_vlr: parcela?.contrFornEvento_vlr ?? 0,
    contrFornEvento_vlrFormatado: currencyFormatter.format(Number(parcela?.contrFornEvento_vlr || 0)),
    ...(nfMeta ? { contrFornEvento_nf: nfMeta } : {}),
  };
};

export function buildContrFornMinutaData({
  contrFornData = {},
  listaParcelas = [],
  selOrcItems = [],
  selPfsFornecedor = [],
  selPjsFornecedor = [],
  selCentroDeCusto = [],
  selProjetos = [],
  selTipoContrato = [],
  selTiposDeMinuta = [],
  documentosObrigatoriosOptions = [],
  showDocumentosObrigatorios: _showDocumentosObrigatorios = false,
  savedAnexos = null,
  filesContrForn = [],
  trimContrFornDesc = () => '',
}) {
  const normalizedOrcItem = selOrcItems.find(
    (item) => Number(item.value) === Number(contrFornData.orcItem_id),
  );
  const contratado = contrFornData.contrForn_parteTab === 'pf'
    ? selPfsFornecedor.find((item) => Number(item.value) === Number(contrFornData.contrForn_parteId))?.label
    : selPjsFornecedor.find((item) => Number(item.value) === Number(contrFornData.contrForn_parteId))?.label;
  const centroDeCusto = selCentroDeCusto.find(
    (item) => Number(item.value) === Number(contrFornData.centroDeCustoRubr_id),
  )?.label;
  const projeto = selProjetos.find(
    (item) => Number(item.orcRubr_id) === Number(contrFornData.orcRubr_id),
  )?.label;
  const tipoContrato = selTipoContrato.find(
    (item) => Number(item.value) === Number(contrFornData.contrFornTp_id),
  )?.label;
  const tipoMinuta = selTiposDeMinuta.find(
    (item) => Number(item.value) === Number(contrFornData.tpMinuta_id),
  )?.label;

  const valorGlobalExibido = contrFornData.contrForn_vlrFixo === 0
    ? listaParcelas.reduce(
      (acc, parcela) => Number(acc) + Number(parcela.contrFornEvento_vlr || 0),
      0,
    )
    : contrFornData.contrForn_vlrGlobal;

  const showEquipeContrFornFields = isEquipeOrcItemById(selOrcItems, contrFornData.orcItem_id);
  const anexosParaExibir = getAnexosParaPdf(savedAnexos, filesContrForn);
  const camposAdicionaisResumo = pdfRowsToObject(buildContrFornStandardExtraPdfRows(contrFornData));
  const documentosObrigatoriosForHash = resolveDocumentosObrigatoriosForHash(
    contrFornData,
    documentosObrigatoriosOptions,
  );

  const minutaData = {
    geradoEm: moment().format('DD/MM/YYYY HH:mm'),
    objetoResumido: formatMinutaText(trimContrFornDesc()),
    centroDeCusto: formatMinutaText(centroDeCusto),
    projeto: formatMinutaText(projeto),
    itemOrcamentario: formatMinutaText(normalizedOrcItem?.label),
    contratado: formatMinutaText(contratado),
    tipoContratado: contrFornData.contrForn_parteTab === 'pf'
      ? 'Pessoa Física'
      : contrFornData.contrForn_parteTab === 'pj'
        ? 'Pessoa Jurídica'
        : null,
    tipoContrato: formatMinutaText(tipoContrato),
    modeloMinuta: formatMinutaText(tipoMinuta),
    vigenciaInicio: contrFornData.contrForn_dtVigIni ?? null,
    vigenciaInicioFormatada: formatMinutaDate(contrFornData.contrForn_dtVigIni),
    vigenciaFim: contrFornData.contrForn_dtVigFim ?? null,
    vigenciaFimFormatada: formatMinutaDate(contrFornData.contrForn_dtVigFim),
    valorGlobal: Number(valorGlobalExibido || 0),
    valorGlobalFormatado: currencyFormatter.format(Number(valorGlobalExibido || 0)),
    ...(contrFornData.contrForn_dtPgtoFixa === 1
      ? { periodicidade: formatMinutaText(contrFornData.contrForn_periodicidade) }
      : {}),
    camposAdicionaisResumo,
    objetoContrato: formatMinutaText(contrFornData.contrForn_obj),
    detalhamentoServico: formatMinutaText(contrFornData.contrForn_detalhamento),
    observacoes: formatMinutaText(contrFornData.contrForn_obs),
    informacoesEquipe: showEquipeContrFornFields
      ? pdfRowsToObject(buildContrFornExtendedFieldsPdfRows(contrFornData))
      : null,
    documentosObrigatorios: documentosObrigatoriosForHash,
    anexos: anexosParaExibir.map((anexo) => ({
      documentoDesc: anexo.documento_desc || anexo.arquivo_caminho || null,
      link: anexo.link || null,
      arquivoCaminho: anexo.arquivo_caminho || null,
    })),
    parcelasData: listaParcelas.map(sanitizeParcelaForMinuta),
  };

  return minutaData;
}
